import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { I } from '../icons.jsx';
import { TextField, Footer, AuthDecor, VividWordmark, FormAlert } from '../primitives.jsx';
import { api, errorMessage } from '../api.js';
import { storage, captureRoutingFromUrl } from '../storage.js';
import { handoffRedirect } from '../sso.js';

// Signup behaves slightly differently depending on signup_source:
//   - portal (or absent)        → companyName is required up-front; the
//                                  user goes through the full onboarding
//                                  wizard before reaching the ERP.
//   - tool:<slug> (e.g. leadstream) → no company collected here; the
//                                  account skips onboarding entirely and
//                                  is locked to that tool's pages.
export default function SignupPage() {
  const navigate = useNavigate();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [company, setCompany] = useState('');
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('');

  useEffect(() => {
    const { source } = captureRoutingFromUrl();
    setSource(source || 'portal');
  }, []);

  const isToolSignup = source.startsWith('tool:');

  const validate = (touch = true) => {
    const e = {};
    if (!first.trim()) e.first = 'First name is required';
    if (!last.trim()) e.last = 'Last name is required';
    if (!email) e.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = 'Enter a valid email address';
    if (!password) e.password = 'Password is required';
    else if (password.length < 8) e.password = 'Use at least 8 characters';
    if (!isToolSignup && !company.trim()) e.company = 'Company name is required';
    if (touch) setErrors(e);
    return e;
  };

  // Leaving a field must not paint "required" on every untouched field:
  // that shifted the layout between mousedown (the blur) and mouseup, so
  // the first click on Create account landed off the button. Only flag
  // fields that hold a value that is wrong.
  const validateOnBlur = () => {
    const e = validate(false);
    const filled = { first, last, email, password, company };
    const quiet = {};
    for (const k of Object.keys(e)) if (filled[k]) quiet[k] = e[k];
    setErrors(quiet);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) return;
    setServerErr('');
    setLoading(true);
    try {
      await api.register({
        firstName: first.trim(),
        lastName: last.trim(),
        email: email.trim(),
        password,
        companyName: isToolSignup ? undefined : company.trim(),
        signupSource: source || 'portal',
      });
      // The source has now been recorded on the user server-side, so drop
      // the stored copy. Leaving it behind would tag the NEXT account
      // created from this browser with the same source, even from a plain
      // portal link with no ``?from=``. nextUrl is kept — onboarding
      // still needs it.
      storage.clearSource();
      // Onboarding step 1 opens with the company typed here.
      if (!isToolSignup) storage.setSignupCompany(company.trim());
      // No email code: the account is already verified, so sign in with the
      // same credentials and carry on exactly as the sign-in page does.
      const auth = await api.login({
        email: email.trim(),
        password,
        nextUrl: storage.getNext() || undefined,
      });
      storage.saveAuth({ token: auth.token, refreshToken: auth.refreshToken, user: auth.user });
      handoffRedirect({
        token: auth.token,
        refreshToken: auth.refreshToken,
        redirectTo: auth.redirectTo,
        navigate,
      });
    } catch (err) {
      setServerErr(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = first.trim() && last.trim() && email.trim() && password && !loading;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <VividWordmark />
            <div className="text-[13px] text-stone-500 hidden sm:block">
              Already have an account?{' '}
              <Link to={`/login${window.location.search}`} className="text-ink font-semibold hover:underline">
                Sign in
              </Link>
            </div>
          </div>

          <div className="flex-1 flex items-start pt-[4vh] justify-center">
            <form onSubmit={submit} className="w-full max-w-[420px]" noValidate>
              <h1 className="text-[28px] font-bold tracking-tight text-ink">
                {isToolSignup
                  ? `Sign up for ${source.split(':')[1] || 'this tool'}`
                  : 'Create your workspace'}
              </h1>
              <p className="mt-2 text-[15px] text-stone-500">
                {isToolSignup
                  ? 'No company setup needed. Once you sign up, open it from your workspace.'
                  : 'Free for 14 days. No credit card required.'}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3">
                <TextField label="First name" value={first} onChange={setFirst}
                  onBlur={validateOnBlur} error={errors.first} autoComplete="given-name" />
                <TextField label="Last name" value={last} onChange={setLast}
                  onBlur={validateOnBlur} error={errors.last} autoComplete="family-name" />
              </div>

              <div className="mt-3 space-y-3">
                <TextField label="Email" type="email" value={email} onChange={setEmail}
                  onBlur={validateOnBlur} placeholder="my@workemail.com"
                  error={errors.email} autoComplete="email" />
                <TextField label="Password" type={showPwd ? 'text' : 'password'} value={password}
                  onChange={setPassword} onBlur={validateOnBlur} placeholder="At least 8 characters"
                  error={errors.password || undefined} autoComplete="new-password"
                  rightAdornment={
                    <button type="button" className="p-1" onClick={() => setShowPwd((s) => !s)}>
                      {showPwd ? <I.EyeOff size={16} /> : <I.Eye size={16} />}
                    </button>
                  }
                />
                {!isToolSignup && (
                  <TextField label="Company name" value={company} onChange={setCompany}
                    onBlur={validateOnBlur} placeholder="Acme Co" error={errors.company}
                    autoComplete="organisation" />
                )}
              </div>

              <div className="pt-7">
                <FormAlert message={serverErr} className="mb-4" />
                <button type="submit" disabled={!canSubmit} className="btn-primary w-full">
                  {loading ? (
                    <>
                      <svg role="status" aria-label="Loading" aria-busy="true" className="w-3.5 h-3.5 animate-spin" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" fill="none" /><path d="M 8 2 a 6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>
                      Creating account…
                    </>
                  ) : (
                    'Create account'
                  )}
                </button>
                <p className="mt-3 text-[12px] text-stone-500 text-center">
                  By creating an account you agree to our Terms and Privacy Policy.
                </p>
              </div>
            </form>
          </div>

          <Footer />
        </div>

        <div className="hidden lg:block">
          <AuthDecor />
        </div>
      </div>
    </div>
  );
}
